import { Button, Select, Table, Tag } from "antd";
import { useState } from "react";
import SearchAndFilter from "../SearchAndFilter/index";
import "./index.scss";
import { pageSizeOptionsPaging } from "utils/constant";
import { useSelector } from "react-redux";
import { toastText } from "utils/utils";
import { postApi } from "redux/apis";

const statusLabel: Record<string, string> = {
  AVAILABLE: "Available",
  LIMITED: "Limited Availability",
  CHECK: "Check Availability",
};

const statusTag = (status: string) => {
  const label = statusLabel[status] || status;
  if (status === "AVAILABLE") return <Tag color="success">{label}</Tag>;
  if (status === "LIMITED") return <Tag color="warning">{label}</Tag>;
  if (status === "CHECK") return <Tag color="error">{label}</Tag>;
  return <Tag>{label}</Tag>;
};

const DynamicTable = (props: any) => {
  // Destructuring props
  const { Column } = Table;
  const {
    itemDataSource,
    performSearchHandler,
    searchValue,
    totalRecords,
    currentPage,
    PageSize,
    modifyPageSize,
    paginationChangeHandler,
    searchFieldsSelectHandler,
    resetButtonHandler,
    searchFields,
    itemModelOptions,
    handleModelChange,
    roleName,
    isCustomerCommercial,
    availabilityFilter,
    availabilityFilterChangeHandler,
  } = props;

  const itemLoading = useSelector((state: any) => state?.qbdItems?.isLoading);
  const [contactSalesLoadingId, setContactSalesLoadingId] = useState<
    number | null
  >(null);

  // Render description with dash if empty
  const renderDescription = (text: string) => {
    return text ? text : "-";
  };

  const contactSalesHandler = async (record: any) => {
    try {
      const shouldContact =
        record?.availabilityOnSite === "CHECK" ||
        record?.availabilityFTZ === "CHECK";

      // Contact sales is only triggered when at least one location is CHECK.
      if (!shouldContact) return;

      const currentId: number | null =
        record?.id != null ? Number(record.id) : null;
      if (currentId != null && contactSalesLoadingId === currentId) return;
      setContactSalesLoadingId(currentId);

      await postApi("/items/contact-sales", {
        itemId: record?.id,
        itemName: record?.name,
        itemDescription: record?.salesDescription,
        availabilityOnSite: record?.availabilityOnSite ?? null,
        availabilityFTZ: record?.availabilityFTZ ?? null,
        quantityOnHandOnsite: record?.quantityOnHandOnsite ?? null,
        quantityOnHandFTZ: record?.quantityOnHandFTZ ?? null,
      });
      toastText("Contact Sales Request Sent Successfully", "success");
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Failed to send Contact Sales Request",
        "error"
      );
    } finally {
      setContactSalesLoadingId(null);
    }
  };

  return (
    <div className={"dynamic-table"}>
      <SearchAndFilter
        performSearchHandler={performSearchHandler}
        searchValue={searchValue}
        PageSize={PageSize}
        searchFieldsSelectHandler={searchFieldsSelectHandler}
        resetButtonHandler={resetButtonHandler}
        searchFields={searchFields}
        menuName="ItemInventory"
        showAvailabilityFilter={isCustomerCommercial}
        availabilityFilterValue={availabilityFilter}
        availabilityFilterChangeHandler={availabilityFilterChangeHandler}
        options={[
          { label: "Description", value: "salesDescription" },
          { label: "Name", value: "name" },
        ]}
      />

      <Table
        dataSource={itemDataSource}
        rowKey="id"
        scroll={{ y: "calc(100vh - 350px)", x: "63vh" }}
        pagination={{
          total: totalRecords,
          current: currentPage,
          onChange: paginationChangeHandler,
          pageSize: PageSize,
          pageSizeOptions: pageSizeOptionsPaging,
          showSizeChanger: true,
          onShowSizeChange: modifyPageSize,
        }}
        loading={itemLoading}
        className="table-global"
      >
        <Column title="Name" dataIndex="name" key="name" className="bg-white" />
        <Column
          title="Description"
          dataIndex="salesDescription"
          key="salesDescription"
          className="bg-white"
          render={renderDescription}
        />
        {isCustomerCommercial ? (
          <>
            <Column
              title="Quantity On Site"
              dataIndex="availabilityOnSite"
              key="availabilityOnSite"
              className="bg-white"
              render={(v: string) => statusTag(v)}
            />
            <Column
              title="Quantity On FTZ"
              dataIndex="availabilityFTZ"
              key="availabilityFTZ"
              className="bg-white"
              render={(v: string) => statusTag(v)}
            />
            <Column
              title="Actions"
              key="contact"
              width={200}
              align="center"
              className="bg-white home-actions-column"
              render={(_, record: any) => (
                record?.availabilityOnSite === "CHECK" ||
                record?.availabilityFTZ === "CHECK" ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%", // ensures centering even if the cell text-align defaults to right
                    }}
                  >
                    <Button
                      type="primary"
                      onClick={() => contactSalesHandler(record)}
                      loading={contactSalesLoadingId === record?.id}
                      style={{
                        borderRadius: 6,
                        padding: "6px 14px",
                        background: "#286fd1",
                        borderColor: "#286fd1",
                        color: "#fff",
                      }}
                    >
                      Contact Sales
                    </Button>
                  </div>
                ) : null
              )}
            />
          </>
        ) : (
          <>
            <Column
              title="Quantity On Site"
              dataIndex="quantityOnHandOnsite"
              key="quantityOnHandOnsite"
              className="bg-white"
            />
            <Column
              title="Quantity On FTZ"
              dataIndex="quantityOnHandFTZ"
              key="quantityOnHandFTZ"
              className="bg-white"
            />
          </>
        )}

        {(roleName === "Admin 3nStar" || roleName === "3nStar Employee 1") && (
          <Column
            title="Item Model"
            key="itemModel"
            className="bg-white"
            render={(text, record) => (
              <Select
                options={itemModelOptions.map(({ id, modelName }) => ({
                  label: modelName,
                  value: id,
                }))}
                placeholder="Select Model"
                onChange={(value) => handleModelChange(value, record)}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option.label as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={record.modelId}
                style={{ width: "100%" }}
              />
            )}
          />
        )}
      </Table>
    </div>
  );
};

export default DynamicTable;
